import { listPopulatedCvFields } from "@/lib/cvBuilderFieldCoverage";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildPdfFileName, useCvBuilder } from "@/stores/cvBuilderStore";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
    AlertTriangle, Check, ChevronDown, ChevronLeft, ChevronRight,
    Code2, Copy, Download, ExternalLink, FileText, Loader2, Maximize2, Minus,
    Plus, RefreshCw, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { normalizeApiError } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import QualityControlPanel from "./QualityControlPanel";

const TECHNICAL_KEYS = new Set([
    "bbox", "charspan", "coord_origin", "reason", "source_ref", "source_refs",
    "text_raw", "unit_id", "no_canonical_target",
]);

const TECHNICAL_LABEL_MAP = {
    significant_block: "Détail complémentaire",
    cv_engine_v2_unresolved_fact: "Information source à vérifier",
    project_title: "Projet / Mission",
    project: "Projet",
    experience: "Expérience",
    work_experience: "Expérience",
    education: "Formation",
    skills: "Compétences",
    skill: "Compétence",
    certifications: "Certification",
    certification: "Certification",
    languages: "Langues",
    language: "Langue",
    summary: "Profil / Résumé",
    contact: "Coordonnées",
    header: "En-tête",
    footer: "Pied de page",
    unknown: "Information complémentaire",
    information: "Information complémentaire",
    text: "Contenu complémentaire",
    section: "Section complémentaire",
};

const sanitizeUnmappedTitle = (rawTitle) => {
    if (!rawTitle) return "Information complémentaire";
    const cleaned = String(rawTitle).trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_");
    if (TECHNICAL_LABEL_MAP[cleaned]) {
        return TECHNICAL_LABEL_MAP[cleaned];
    }
    if (cleaned.includes("significant_block")) return "Détail complémentaire";
    if (cleaned.includes("project_title")) return "Projet / Mission";
    if (cleaned.includes("coord") || cleaned.includes("bbox") || cleaned.includes("span")) return "Note complémentaire";

    const readable = String(rawTitle).replaceAll("_", " ").trim();
    return readable.charAt(0).toUpperCase() + readable.slice(1);
};

const normalizeForMatch = (value) => String(value || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\W+/g, " ").trim();

const readableUnmapped = (item) => {
    if (typeof item === "string") return { title: "Information complémentaire", content: item };
    if (!item || typeof item !== "object") return null;
    const content = item.content ?? item.text ?? item.text_raw ?? item.value ?? "";
    const rawTitle = item.section ?? item.label ?? item.title ?? item.type ?? "Information";
    if (typeof content !== "string" && typeof content !== "number") return null;
    return { title: sanitizeUnmappedTitle(rawTitle), content: String(content).trim() };
};

const getRelevantUnmapped = (draft) => {
    const mappedValues = listPopulatedCvFields(draft || {})
        .flatMap(({value}) => Array.isArray(value) ? value : [value])
        .filter(value => typeof value === 'string')
        .map(normalizeForMatch).filter(value => value.length >= 3);

    return (draft?.unmapped_content || []).map(readableUnmapped).filter(Boolean).filter(item => {
        const text = normalizeForMatch(item.content);
        // Hide only a complete fact already represented in a business field.
        // A larger source block containing one known skill may still include
        // missing information, so never discard it on that partial match.
        return item.content && !mappedValues.some(value => text.length >= 3 && (` ${value} `).includes(` ${text} `));
    });
};

const warningCatalog = {
    incoherent_chronology: { title: "Chronologie à vérifier", body: "Certaines expériences professionnelles semblent se chevaucher.", step: 2 },
    chronology_overlap: { title: "Chronologie à vérifier", body: "Certaines expériences professionnelles semblent se chevaucher.", step: 2 },
    summary_missing: { title: "Profil à vérifier", body: "Le résumé professionnel mérite une relecture.", step: 0, fieldPath: "candidate.summary" },
    certifications_missing: { title: "Formation à vérifier", body: "Vérifiez les certifications et formations du candidat.", step: 4, fieldPath: "certifications" },
    languages_missing: { title: "Langues à vérifier", body: "Vérifiez les langues et leurs niveaux.", step: 4, fieldPath: "languages" },
    skills_missing: { title: "Compétences à vérifier", body: "Vérifiez les compétences principales du candidat.", step: 1, fieldPath: "skills.highlighted" },
};

const toBusinessWarning = (warning) => {
    const raw = typeof warning === "string" ? warning : warning?.code || warning?.type || warning?.message || "";
    const normalized = String(raw).toLowerCase();
    const advisoryText = `${raw} ${warning?.message || ''}`.toLowerCase();
    if (/information uniquement|import continue|soft_limit|advisory/.test(advisoryText)) {
        return {title: "Information d’utilisation IA", body: "Le seuil quotidien d’imports a été atteint. Cette information n’empêche pas l’import et ne demande aucune correction du CV.", informational: true, id: raw};
    }
    const match = Object.entries(warningCatalog).find(([key]) => normalized.includes(key));
    if (match) return { ...match[1], id: `${match[0]}-${raw}` };
    if (normalized.includes("chronolog") || normalized.includes("overlap") || normalized.includes("chevauch")) {
        return { ...warningCatalog.incoherent_chronology, id: raw };
    }
    return { title: "Information à examiner", body: "Consultez le contrôle qualité pour identifier les informations du CV à relire.", fieldPath: warning?.field_path, step: null, id: raw || "review" };
};

const downloadBlob = (blob, fileName, mimeType) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const goToStep = (target) => window.dispatchEvent(new CustomEvent("cvbuilder:go-to-step", { detail: typeof target === "object" ? target : { step: target } }));

const provenanceLabel = (value) => ({ ENRICHED: "COMPLÉTÉ", USER_EDITED: "MODIFIÉ", SOURCE: "SOURCE" }[value] || "COMPLÉTÉ");

const calculateTotalPages = (draft, apiPages) => {
    if (apiPages && Number.isFinite(apiPages) && apiPages > 0) {
        return apiPages;
    }
    const expCount = draft?.experiences?.length || 0;
    if (expCount >= 8) return 6;
    if (expCount >= 5) return 4;
    if (expCount >= 3) return 3;
    if (expCount >= 1) return 2;
    return 1;
};

const PreviewStep = () => {
    const {
        draft, generateDraft, previewDraftPdf, saveDraft, completionScore = 0, parseScore = 0,
        importWarnings = [], mandatoryReview, userValidated, validateDraft,
        previewPageCount, anomalies = [], scoringDimensions, resolveAnomaly,
    } = useCvBuilder();

    // Accordion states: CLOSED BY DEFAULT
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isUnmappedOpen, setIsUnmappedOpen] = useState(false);
    const [isQualityControlOpen, setIsQualityControlOpen] = useState(false);

    // Preview state
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewError, setPreviewError] = useState("");
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [detectedPages, setDetectedPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState(100);
    const [exporting, setExporting] = useState("");
    const [isValidating, setIsValidating] = useState(false);

    // JSON inspection modal state
    const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
    const [isJsonCopied, setIsJsonCopied] = useState(false);

    const activeBlobUrlRef = useRef("");
    const lastGeneratedFingerprintRef = useRef("");

    const candidate = useMemo(() => draft?.candidate || {}, [draft?.candidate]);
    const qualityScore = Math.max(0, Math.min(100, Number(parseScore || completionScore || 0)));
    const warnings = useMemo(() => importWarnings.map(toBusinessWarning), [importWarnings]);
    const unmapped = useMemo(() => getRelevantUnmapped(draft), [draft]);
    const provenance = useMemo(() => draft?.source_metadata?.field_provenance || {}, [draft?.source_metadata?.field_provenance]);

    const totalPages = useMemo(() => {
        return calculateTotalPages(draft, detectedPages || previewPageCount);
    }, [draft, detectedPages, previewPageCount]);

    const draftFingerprint = useMemo(() => {
        return JSON.stringify({
            candidate: draft?.candidate,
            skills: draft?.skills,
            experiences: draft?.experiences,
            education: draft?.education,
            languages: draft?.languages,
            certifications: draft?.certifications,
            projects: draft?.projects,
            additionalSections: draft?.additional_sections,
            unmapped: draft?.unmapped_content,
            theme: draft?.theme,
            trigram: draft?.trigram,
        });
    }, [draft]);

    const enriched = useMemo(() => {
        const items = [];
        if (candidate.years_of_experience == null && candidate.computed_years_of_experience != null) {
            items.push({ label: `${candidate.computed_years_of_experience} ans d’expérience`, detail: "Calculé depuis les expériences", badge: "CALCULÉ" });
        }
        if (candidate.subtitle && provenance["candidate.subtitle"] && provenance["candidate.subtitle"] !== "SOURCE") {
            items.push({ label: candidate.subtitle, detail: "Accroche proposée", badge: provenanceLabel(provenance["candidate.subtitle"]) });
        }
        if (candidate.summary && provenance["candidate.summary"] && provenance["candidate.summary"] !== "SOURCE") {
            items.push({ label: "Résumé professionnel", detail: "Généré à partir du parcours", badge: provenanceLabel(provenance["candidate.summary"]) });
        }
        if (draft?.skills?.highlighted?.length && provenance["skills.highlighted"] && provenance["skills.highlighted"] !== "SOURCE") {
            items.push({ label: "Compétences phares", detail: draft.skills.highlighted.join(" · "), badge: provenanceLabel(provenance["skills.highlighted"]) });
        }
        return items;
    }, [candidate, draft?.skills?.highlighted, provenance]);

    const sections = [
        ["Identité", Boolean(candidate.full_name || candidate.title), 0],
        ["Compétences", Boolean(draft?.skills?.highlighted?.length || draft?.skills?.categories?.length), 1],
        [`${draft?.experiences?.length || 0} expérience${draft?.experiences?.length > 1 ? "s" : ""}`, Boolean(draft?.experiences?.length), 2],
        ["Formation", Boolean(draft?.education?.length), 4],
        ["Langues", Boolean(draft?.languages?.length), 4],
    ].filter(([, present]) => present);

    const canPreview = Boolean(candidate.full_name || candidate.title || draft?.experiences?.length || draft?.skills?.categories?.length);

    // Fetch / generate PDF with proper cache and lifecycle cleanup
    const handlePreviewPdf = useCallback(async (_force = false) => {
        if (!canPreview) return;
        setIsPreviewLoading(true);
        setPreviewError("");
        try {
            const result = await previewDraftPdf();
            const nextUrl = typeof result === "string" ? result : result?.url;
            const apiPages = typeof result === "object" ? result?.totalPages : null;

            if (apiPages && Number.isFinite(apiPages)) {
                setDetectedPages(apiPages);
            }

            // Cleanup previous blob URL if different to avoid memory leak
            if (activeBlobUrlRef.current && activeBlobUrlRef.current !== nextUrl) {
                window.URL.revokeObjectURL(activeBlobUrlRef.current);
            }
            activeBlobUrlRef.current = nextUrl;
            lastGeneratedFingerprintRef.current = draftFingerprint;
            setPreviewUrl(nextUrl);
        } catch (error) {
            setPreviewError(normalizeApiError(error, "Le service d’aperçu ne répond pas. Réessayez dans quelques instants.").message);
        } finally {
            setIsPreviewLoading(false);
        }
    }, [canPreview, draftFingerprint, previewDraftPdf]);

    // Handle accordion open/close toggle
    const handleTogglePreview = (open) => {
        setIsPreviewOpen(open);
        if (open) {
            // Lazy load on first open, or if draft changed since last generation
            const isOutdated = lastGeneratedFingerprintRef.current !== draftFingerprint;
            if (!previewUrl || isOutdated) {
                handlePreviewPdf();
            }
        }
    };

    // Clean up active blob URL when component unmounts
    useEffect(() => {
        return () => {
            if (activeBlobUrlRef.current) {
                window.URL.revokeObjectURL(activeBlobUrlRef.current);
                activeBlobUrlRef.current = "";
            }
        };
    }, []);

    const handleExport = async (format) => {
        setExporting(format);
        try {
            const generated = await generateDraft(format);
            if (!generated?.blob) return;
            downloadBlob(generated.blob, generated.fileName, format === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf");
        } finally {
            setExporting("");
        }
    };

    const handleValidate = async () => {
        setIsValidating(true);
        try {
            const validated = validateDraft ? await validateDraft() : Boolean(await saveDraft?.());
            if (validated) toast.success("CV validé pour l’export");
        } finally {
            setIsValidating(false);
        }
    };

    const handleCopyJson = async () => {
        const jsonText = JSON.stringify(draft, null, 2);
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(jsonText);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = jsonText;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                textArea.remove();
            }
            setIsJsonCopied(true);
            toast.success("JSON copié dans le presse-papiers");
            window.setTimeout(() => setIsJsonCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier le JSON");
        }
    };

    const handleDownloadJson = () => {
        const jsonText = JSON.stringify(draft, null, 2);
        const trigram = String(draft?.trigram || "CAN").trim().toUpperCase() || "CAN";
        downloadBlob(new Blob([jsonText], { type: "application/json" }), `DC_${trigram}_CV.json`, "application/json");
        toast.success("JSON téléchargé");
    };

    return (
        <div className="space-y-6" data-testid="preview-step">
            {/* Header */}
            <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-500">
                        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                        Studio de finalisation
                    </div>
                    <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                        Vérifiez le CV avant génération
                    </h2>
                </div>
                <Badge variant="outline" className="w-fit border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300">
                    Étape 6 / 6
                </Badge>
            </header>

            {/* 1. Qualité du CV */}
            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900" aria-label="Qualité du CV">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-slate-950 dark:text-white">Qualité du CV</h3>
                        <p className="mt-0.5 text-xs text-slate-500">Score de complétion et conformité LENVIA</p>
                    </div>
                    <div className="text-right">
                        <span className="text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
                            {qualityScore}%
                        </span>
                    </div>
                </div>
                <Progress
                    value={qualityScore}
                    className="mt-3 h-2 bg-slate-100 dark:bg-white/10 [&>div]:bg-blue-600"
                    aria-label={`Qualité du CV : ${qualityScore}%`}
                />
                <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                    {sections.map(([label, , step]) => (
                        <li key={label} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50">
                            <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-300">
                                <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                                {label}
                            </span>
                            <button
                                type="button"
                                className="text-xs text-blue-600 underline-offset-4 hover:underline focus-visible:outline-none dark:text-blue-400"
                                onClick={() => goToStep(step)}
                            >
                                Modifier
                            </button>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Contrôle qualité de l'extraction */}
            <section className="rounded-xl border border-blue-500/30 bg-blue-50/70 p-5 dark:bg-blue-950/20 dark:border-blue-500/30 shadow-sm" aria-label="Contrôle qualité de l'extraction">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start sm:items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                            <ShieldCheck className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-950 dark:text-white">
                                    Contrôle qualité de l'extraction
                                </h3>
                                {anomalies.filter((a) => a.status !== "resolved" && a.status !== "verified").length > 0 ? (
                                    <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 text-xs font-semibold">
                                        {anomalies.filter((a) => a.status !== "resolved" && a.status !== "verified").length} point(s) à examiner
                                    </Badge>
                                ) : (draft?.source_metadata?.document_quality?.documentary_verification_current === false || draft?.source_metadata?.document_quality?.decision === "TECHNICAL_INTEGRITY_ONLY") ? (
                                    <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30 text-xs font-semibold">
                                        Intégrité technique validée (Édité)
                                    </Badge>
                                ) : (draft?.source_metadata?.document_quality?.decision === "APPROVED" || draft?.source_metadata?.document_quality?.status === "ok") && draft?.source_metadata?.document_quality?.documentary_verification_current !== false ? (
                                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-xs font-semibold">
                                        ✓ Vérifié
                                    </Badge>
                                ) : (
                                    <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30 text-xs font-semibold">
                                        Intégrité technique validée
                                    </Badge>
                                )}
                            </div>
                            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                                Analyse multidimensionnelle : vérifiez les correspondances avec le CV source et réaffectez les données.
                            </p>
                        </div>
                    </div>
                    <Button
                        type="button"
                        onClick={() => setIsQualityControlOpen(true)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs h-9 px-4 rounded-xl shadow-sm shrink-0"
                    >
                        Examiner les anomalies
                    </Button>
                </div>
            </section>

            {/* Warnings if any */}
            {warnings.length > 0 && (
                <section className="rounded-xl border border-amber-500/30 bg-amber-50/70 p-4 dark:bg-amber-500/10" aria-label="Points d'attention">
                    <div className="flex items-center gap-2 font-semibold text-amber-950 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <span>{warnings.every(warning => warning.informational) ? "Informations" : `À vérifier (${warnings.filter(warning => !warning.informational).length})`}</span>
                    </div>
                    <div className="mt-2 space-y-2">
                        {warnings.map((warning) => (
                            <div key={warning.id} className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-300">
                                <span><strong>{warning.title}</strong> — {warning.body}</span>
                                {!warning.informational && <Button
                                    variant="link"
                                    className="h-auto p-0 text-xs text-blue-600 dark:text-blue-400"
                                    onClick={() => warning.step == null && !warning.fieldPath ? setIsQualityControlOpen(true) : goToStep({step: warning.step, fieldPath: warning.fieldPath, title: warning.title, message: warning.body})}
                                >
                                    {warning.step == null && !warning.fieldPath ? 'Examiner' : 'Corriger'}
                                </Button>}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 2. Informations complétées */}
            {enriched.length > 0 && (
                <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900" aria-label="Informations complétées">
                    <h3 className="font-semibold text-slate-950 dark:text-white">
                        Informations complétées
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                        Éléments ajoutés ou calculés pour finaliser le CV.
                    </p>
                    <div className="mt-3 divide-y divide-slate-100 dark:divide-white/5">
                        {enriched.map((item) => (
                            <div key={`${item.label}-${item.detail}`} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                                <div className="min-w-0 pr-4">
                                    <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                                        {item.label}
                                    </p>
                                    {item.detail && item.detail !== item.label && (
                                        <p className="truncate text-xs text-slate-500">
                                            {item.detail}
                                        </p>
                                    )}
                                </div>
                                <Badge variant="outline" className="shrink-0 border-blue-500/30 bg-blue-500/10 text-[10px] font-semibold text-blue-700 dark:text-blue-300">
                                    {item.badge}
                                </Badge>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 3. Informations non intégrées — Compact row CLOSED BY DEFAULT */}
            {unmapped.length > 0 && (
                <Collapsible
                    open={isUnmappedOpen}
                    onOpenChange={setIsUnmappedOpen}
                    className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900"
                >
                    <CollapsibleTrigger
                        className="flex w-full items-center justify-between p-4 sm:p-5 text-left transition-colors hover:bg-slate-50/75 dark:hover:bg-slate-800/40"
                        data-testid="unmapped-accordion-trigger"
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 dark:text-white">
                                Informations non intégrées
                            </span>
                            <span className="text-slate-400 dark:text-slate-500">—</span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {unmapped.length} élément{unmapped.length > 1 ? "s" : ""} à examiner
                            </span>
                        </div>
                        <ChevronDown
                            className={`h-5 w-5 text-slate-500 transition-transform duration-200 ${isUnmappedOpen ? "rotate-180" : ""}`}
                            aria-hidden="true"
                        />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="border-t border-slate-200 p-4 sm:p-5 dark:border-white/10 space-y-3">
                        {unmapped.map((item, index) => (
                            <div key={`${item.title}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 dark:border-white/10 dark:bg-slate-800/40">
                                <p className="text-xs font-semibold text-slate-900 dark:text-white">
                                    {item.title}
                                </p>
                                <p className="mt-1 overflow-wrap-anywhere text-sm text-slate-600 dark:text-slate-300">
                                    {item.content}
                                </p>
                                <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                                    Non intégré automatiquement au modèle
                                </p>
                            </div>
                        ))}
                    </CollapsibleContent>
                </Collapsible>
            )}

            {/* 4. Aperçu du CV — Accordion CLOSED BY DEFAULT */}
            <Collapsible
                open={isPreviewOpen}
                onOpenChange={handleTogglePreview}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900"
            >
                <CollapsibleTrigger
                    className="flex w-full items-center justify-between p-4 sm:p-5 text-left transition-colors hover:bg-slate-50/75 dark:hover:bg-slate-800/40"
                    data-testid="preview-accordion-trigger"
                >
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            Aperçu du CV
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-500">
                            Document PDF • {totalPages} page{totalPages > 1 ? "s" : ""}
                        </p>
                    </div>
                    <ChevronDown
                        className={`h-5 w-5 text-slate-500 transition-transform duration-200 ${isPreviewOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    />
                </CollapsibleTrigger>

                <CollapsibleContent className="border-t border-slate-200 dark:border-white/10">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/90 px-4 py-2.5 dark:border-white/10 dark:bg-slate-950/60">
                        {/* Page navigation */}
                        <div className="flex items-center gap-1.5">
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage <= 1 || isPreviewLoading}
                                aria-label="Page précédente"
                                data-testid="preview-prev-page"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="min-w-[5.5rem] text-center text-xs font-medium tabular-nums text-slate-700 dark:text-slate-300" data-testid="preview-page-indicator">
                                Page {currentPage} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages || isPreviewLoading}
                                aria-label="Page suivante"
                                data-testid="preview-next-page"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Zoom controls */}
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setZoom((value) => Math.max(50, value - 25))}
                                aria-label="Réduire le zoom"
                            >
                                <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-12 text-center text-xs tabular-nums text-slate-600 dark:text-slate-300">
                                {zoom}%
                            </span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setZoom((value) => Math.min(150, value + 25))}
                                aria-label="Augmenter le zoom"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-xs text-slate-700 dark:text-slate-300"
                                onClick={() => setZoom(100)}
                            >
                                <Maximize2 className="mr-1.5 h-3.5 w-3.5" />
                                Ajuster
                            </Button>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-xs text-slate-700 dark:text-slate-300"
                                onClick={() => handlePreviewPdf(true)}
                                disabled={isPreviewLoading || !canPreview}
                                data-testid="preview-refresh-btn"
                            >
                                <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isPreviewLoading ? "animate-spin" : ""}`} />
                                Actualiser
                            </Button>
                            {previewUrl && (
                                <>
                                    <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-700 dark:text-slate-300" asChild>
                                        <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                            Ouvrir
                                        </a>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-700 dark:text-slate-300" asChild>
                                        <a href={previewUrl} download={buildPdfFileName(draft)}>
                                            <Download className="mr-1.5 h-3.5 w-3.5" />
                                            Télécharger
                                        </a>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Viewer Content — 100% full width, no sidebar */}
                    {isPreviewLoading && (
                        <div className="flex min-h-[640px] flex-col items-center justify-center gap-4 bg-slate-100 p-8 dark:bg-slate-950 text-center" role="status" aria-live="polite">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                            <div>
                                <p className="font-semibold text-slate-900 dark:text-white">
                                    Génération de l'aperçu PDF en cours...
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Cela peut prendre quelques secondes.
                                </p>
                            </div>
                        </div>
                    )}

                    {!isPreviewLoading && previewError && (
                        <div className="flex min-h-[480px] items-center justify-center bg-slate-100 p-6 dark:bg-slate-950">
                            <div className="max-w-md rounded-xl border border-red-500/25 bg-white p-7 text-center shadow-sm dark:bg-slate-900">
                                <AlertTriangle className="mx-auto h-8 w-8 text-red-500" />
                                <h4 className="mt-3 font-semibold text-slate-950 dark:text-white">
                                    Impossible de générer l'aperçu
                                </h4>
                                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                    {previewError}
                                </p>
                                <Button className="mt-5" onClick={() => handlePreviewPdf(true)} data-testid="preview-retry-btn">
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Réessayer
                                </Button>
                            </div>
                        </div>
                    )}

                    {!isPreviewLoading && !previewError && !canPreview && (
                        <div className="flex min-h-[480px] items-center justify-center bg-slate-100 p-6 text-center dark:bg-slate-950">
                            <div>
                                <FileText className="mx-auto h-10 w-10 text-slate-400" />
                                <h4 className="mt-3 font-semibold text-slate-950 dark:text-white">
                                    Aperçu indisponible
                                </h4>
                                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                    Complétez les informations du candidat pour générer l'aperçu.
                                </p>
                            </div>
                        </div>
                    )}

                    {!isPreviewLoading && !previewError && previewUrl && (
                        <div className="w-full overflow-auto bg-slate-200/70 p-4 dark:bg-slate-950 sm:p-6 flex justify-center" data-testid="pdf-inline-preview">
                            <div
                                className="mx-auto origin-top overflow-hidden bg-white shadow-2xl transition-transform motion-reduce:transition-none"
                                style={{ width: `${zoom}%`, maxWidth: "920px" }}
                            >
                                <iframe
                                    key={`${previewUrl}-page-${currentPage}`}
                                    src={`${previewUrl}#page=${currentPage}&toolbar=0&navpanes=0&view=FitH`}
                                    title="Aperçu du CV"
                                    className="block h-[820px] w-full border-0 sm:h-[940px] bg-white"
                                    data-testid="cv-pdf-iframe"
                                />
                            </div>
                        </div>
                    )}
                </CollapsibleContent>
            </Collapsible>

            {/* Sticky footer actions with visible WCAG AA buttons */}
            <footer className="sticky bottom-3 z-20 flex flex-col gap-3 rounded-xl border border-slate-300 bg-white/95 p-3.5 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => window.dispatchEvent(new Event("cvbuilder:prev-step"))}
                        className="border border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 font-medium shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
                        data-testid="preview-prev-btn"
                    >
                        Précédent
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsJsonModalOpen(true)}
                        className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 font-medium shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
                        data-testid="view-json-btn"
                    >
                        <Code2 className="mr-1.5 h-4 w-4 text-blue-600 dark:text-blue-400" />
                        Voir le JSON
                    </Button>
                </div>
                <div className="grid gap-2 sm:flex sm:items-center">
                    {mandatoryReview && !userValidated ? (
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                            Certaines informations peuvent nécessiter une relecture. L’export reste disponible.
                        </span>
                    ) : null}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleExport("docx")}
                        disabled={Boolean(exporting)}
                        title="Télécharger le document Word"
                        className="border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:border-blue-400 dark:border-blue-800/80 dark:bg-blue-950/60 dark:text-blue-200 dark:hover:bg-blue-900/60 font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-blue-500"
                        data-testid="generate-docx-btn"
                    >
                        <Download className="mr-2 h-4 w-4 text-blue-700 dark:text-blue-300" />
                        {exporting === "docx" ? "Génération Word…" : "Télécharger Word"}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleExport("pdf")}
                        disabled={Boolean(exporting)}
                        title="Télécharger le document PDF"
                        className="border border-blue-600 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-700 dark:border-blue-500 dark:bg-slate-900 dark:text-blue-300 dark:hover:bg-blue-950/50 font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-blue-500"
                        data-testid="generate-pdf-btn"
                    >
                        <Download className="mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" />
                        {exporting === "pdf" ? "Génération PDF…" : "Télécharger PDF"}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleValidate}
                        disabled={isValidating}
                        className="bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 font-semibold shadow-md transition-colors disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        data-testid="validate-cv-btn"
                    >
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        {isValidating ? "Enregistrement…" : "Marquer comme relu (facultatif)"}
                    </Button>
                </div>
            </footer>

            {/* Modal de consultation du JSON canonique */}
            <Dialog open={isJsonModalOpen} onOpenChange={setIsJsonModalOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-6 bg-slate-900 border border-slate-700 text-slate-100 shadow-2xl">
                    <DialogHeader className="space-y-1">
                        <DialogTitle className="text-xl font-bold flex items-center gap-2 text-white">
                            <Code2 className="h-5 w-5 text-blue-400" />
                            Structure JSON Canonique du CV
                        </DialogTitle>
                        <DialogDescription className="text-slate-400 text-xs">
                            JSON canonique synchronisé en temps réel avec les modifications du brouillon LENVIA.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="relative flex-1 overflow-hidden my-4 border border-slate-800 rounded-lg bg-slate-950">
                        <pre className="h-full max-h-[55vh] overflow-auto p-4 text-xs font-mono text-emerald-400 select-all whitespace-pre leading-relaxed">
                            {JSON.stringify(draft, null, 2)}
                        </pre>
                    </div>

                    <DialogFooter className="flex items-center justify-between gap-3 pt-2 border-t border-slate-800 sm:justify-between">
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                onClick={handleCopyJson}
                                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-blue-400"
                                data-testid="copy-json-btn"
                            >
                                {isJsonCopied ? (
                                    <>
                                        <Check className="mr-1.5 h-3.5 w-3.5 text-white" />
                                        Copié !
                                    </>
                                ) : (
                                    <>
                                        <Copy className="mr-1.5 h-3.5 w-3.5" />
                                        Copier le JSON
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleDownloadJson}
                                className="border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs font-medium"
                                data-testid="download-json-btn"
                            >
                                <Download className="mr-1.5 h-3.5 w-3.5" />
                                Télécharger JSON
                            </Button>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsJsonModalOpen(false)}
                            className="border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 text-xs"
                        >
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <QualityControlPanel
                isOpen={isQualityControlOpen}
                onClose={() => setIsQualityControlOpen(false)}
                anomalies={anomalies}
                scoringDimensions={scoringDimensions}
                totalScore={qualityScore}
                onResolveAnomaly={resolveAnomaly}
                onNavigateToStep={goToStep}
            />
        </div>
    );
};

export { TECHNICAL_KEYS, getRelevantUnmapped, toBusinessWarning, sanitizeUnmappedTitle };
export default PreviewStep;
